import type { BlobModel } from '@/models/blob';

/**
 * How long to wait before each attempt at refetching a blob's metadata.
 *
 * Six attempts over a minute, which is the window the worker takes to decode a 12MP photo and
 * write both derivatives, queue included. The SP Data app waits on the same schedule
 * (`useDerivativePoll.ts` there), so a photo arrives on both clients at the same moment.
 */
export const DERIVATIVE_POLL_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000];

/**
 * Whether this blob has a derivative still to come: stored, no thumbnail yet, and the worker has
 * not reported that it could not convert it.
 *
 * Nothing about the file's format. The worker sniffs the `ftyp` brand from the file's own bytes
 * rather than trusting the content type the browser guessed, so whether a derivative is coming is
 * its business. The only question a client can answer honestly is whether one has arrived. The
 * cost is that a PDF spends the six attempts once, then stops.
 */
export const awaitsDerivative = (blob: BlobModel) =>
  !!blob.storedAt && !blob.hasThumbnail && !blob.conversionFailed;

interface DerivativePoll {
  fetch: () => Promise<BlobModel>;
  /** Every answer that arrives, so the tile can take a failure marker as well as a thumbnail. */
  onAnswer: (blob: BlobModel) => void;
  /** Read before every attempt, so a deleted file or a closed entry stops the asking. */
  stillWanted: () => boolean;
}

/**
 * Refetches a blob's metadata until its derivatives land, the worker gives up on it, or the
 * attempts run out. Running out is an ordinary outcome. The tile keeps whatever it was already
 * showing, the local preview or the file icon.
 */
export const pollForDerivative = async ({ fetch, onAnswer, stillWanted }: DerivativePoll) => {
  for (const delay of DERIVATIVE_POLL_DELAYS_MS) {
    await new Promise(resolve => setTimeout(resolve, delay));
    if (!stillWanted()) {
      return;
    }
    let answer: BlobModel;
    try {
      answer = await fetch();
    } catch {
      // A metadata or thumbnail request that failed says nothing about whether the worker is done.
      continue;
    }
    // `BlobService` answers with a blob or throws. This guards an adapter that breaks the port, the
    // way `readSlot` in `fileUploads.ts` does, rather than handing `onAnswer` nothing to write. It
    // ends the poll instead of retrying like a failed request, because that adapter won't recover.
    if (!answer) {
      return;
    }
    onAnswer(answer);
    if (!awaitsDerivative(answer)) {
      return;
    }
  }
};
